import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Box, 
  IconButton, 
  Avatar, 
  Fade, 
  useMediaQuery, 
  Snackbar, 
  Alert 
} from '@mui/material';
import { ThemeProvider, styled, useTheme, createTheme } from '@mui/material/styles';
import { Settings as SettingsIcon } from 'lucide-react';
import ProfileDashboard from '../ProfileDashboard';
import HomeInput from '../../HomeInput/HomeInput';
import ChatViewNest from '../../../AGTChat/core/ChatViewNest';
import SettingsDialog from '../../navigation/Settings';
import NavigationBar from '../../navigation/NavigationBar';
import LaunchpadButton from '../../navigation/LaunchpadButton';
import SelectApps from '../../SelectApps/SelectApps';
// Imports from AGTChat no longer needed - message sending is handled by parent (App.js)
import {
  getUserBackgroundPreference,
  setUserBackgroundPreference,
  getUserStartupTabPreference,
  setUserStartupTabPreference,
  getUserLanguagePreference,
  setUserLanguagePreference,
  getUserWallpaperPreference,
  checkProStatus,
  saveUserSelectedApps,
  getUserSelectedApps
} from '../../services/AGTHallService';
import { isAuthorized } from '../../auth/AuthorizedUsers';
import AGTUps from '../../../AGTUps/AGTUps';
import AGTEdu from '../../../AGTEdu/AGTEdu';
import AGTFin from '../../../AGTFin/AGTFin'; // NUEVO IMPORT PARA AGT FIN
import AGTPromo from '../../../AGTPromo/AGTPromo';
import AGTHealth from '../../../AGTHealth/AGTHealth';
import AGTFit from '../../../AGTFit/AGTFit';
import { getDashboardStyles } from './dashboardStyles';
import { getFirstName } from '../../../../utils/nameUtils';
import { useTranslation } from 'react-i18next';
import { getTheme } from '../../../../theme';

const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 horas
const MESSAGE_CACHE_SIZE = 100;
const NAVIGATION_DEBOUNCE_TIME = 300;

const StyledContainer = styled(Box)(({ theme }) => ({
  ...getDashboardStyles(theme, 'none').fixedContainer,
  overflow: 'hidden' // Asegurarse de que el contenedor principal no tiene scroll
}));

const StyledWindowBar = styled(Box)(({ theme }) => ({
  ...getDashboardStyles(theme, 'none').windowBar
}));

const TopBar = styled(Box)(({ theme }) => ({
  ...getDashboardStyles(theme, 'none').topBar,
  position: 'relative',
  height: '48px',
  marginBottom: 0, // Ensure no margin
  [theme.breakpoints.down('sm')]: {
    height: '44px' // Slightly shorter on mobile
  }
}));

const NavContainer = styled(Box)(({ theme }) => ({
  ...getDashboardStyles(theme, 'none').navContainer
}));

const ContentArea = styled(Box)(({ theme }) => ({
  ...getDashboardStyles(theme, 'none').contentArea,
  marginTop: 0 // Ensure no extra margin
}));

// Clase para cachear mensajes
class MessageCache {
  constructor(maxSize = MESSAGE_CACHE_SIZE) {
    this.maxSize = maxSize;
    this.cache = new Map();
  }

  add(chatId, message) {
    if (!this.cache.has(chatId)) {
      this.cache.set(chatId, []);
    }
    const messages = this.cache.get(chatId);
    messages.push(message);
    
    if (messages.length > this.maxSize) {
      messages.shift();
    }
  }

  get(chatId) {
    return this.cache.get(chatId) || [];
  }

  clear() {
    this.cache.clear();
  }
}

// Clase para manejo de recursos
class ResourceManager {
  constructor() {
    this.resources = new Set();
    this.cleanupFunctions = new Map();
  }

  register(resource, cleanup) {
    this.resources.add(resource);
    if (cleanup) {
      this.cleanupFunctions.set(resource, cleanup);
    }
  }

  release(resource) {
    const cleanup = this.cleanupFunctions.get(resource);
    if (cleanup) {
      cleanup();
      this.cleanupFunctions.delete(resource);
    }
    this.resources.delete(resource);
  }

  clearAll() {
    this.cleanupFunctions.forEach((cleanup) => cleanup());
    this.cleanupFunctions.clear();
    this.resources.clear();
  }
}

const TopBarContent = React.memo(({ 
  handleBackgroundClick, 
  handleProfileClick, 
  user, 
  onNavigate, 
  activeSection, 
  showUpsInNav, 
  showEduInNav,
  showFinInNav, // NUEVO PROP PARA FIN
  showHealthInNav,
  showFitInNav,
  selectedApps
}) => {
  const theme = useTheme();
  const styles = getDashboardStyles(theme, activeSection);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
 
  return (
    <TopBar>
      {/* On desktop: Show Settings icon on the left */}
      {!isMobile && (
        <IconButton
          onClick={handleBackgroundClick}
          sx={{
            ...styles.backgroundButton(isMobile),
            '& svg': {
              // Blanco en wallpaper, azul en otros modos
              color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#007BFE',
              transition: 'all 0.2s'
            },
            '&:hover svg': {
              opacity: 0.8
            },
            position: 'absolute',
            left: '10px',
          }}
        >
          <SettingsIcon size={24} />
        </IconButton>
      )}

      {/* On mobile: Show LaunchpadButton on the left instead of Settings */}
      {isMobile && (
        <Box sx={{ 
          position: 'absolute', 
          left: '8px'
        }}>
          <LaunchpadButton onNavigate={onNavigate} />
        </Box>
      )}

      {/* Mostrar Launchpad solo en escritorio */}
      {!isMobile && (
        <Box sx={{ 
          position: 'absolute', 
          right: '56px'
        }}>
          <LaunchpadButton onNavigate={onNavigate} />
        </Box>
      )}

      <NavContainer sx={{ flex: 1, justifyContent: 'center', display: 'flex' }}>
        <NavigationBar 
          onNavigate={onNavigate} 
          activeSection={activeSection}
          showUpsInNav={showUpsInNav}
          showEduInNav={showEduInNav}
          showFinInNav={showFinInNav} // NUEVO PROP PARA FIN
          showHealthInNav={showHealthInNav}
          showFitInNav={showFitInNav}
          selectedApps={selectedApps}
        />
      </NavContainer>

      <Avatar 
        src={user?.photoURL || "/path-to-default-image.jpg"} 
        alt={user?.displayName || "Profile"} 
        onClick={handleProfileClick}
        sx={{
          ...styles.avatar(isMobile),
          position: 'absolute',
          right: isMobile ? '12px' : '12px',
        }}
      />
    </TopBar>
  );
});

const FullDashboard = ({
  onLogout,
  user: initialUser,
  backgroundType: initialBackgroundType,
  onBackgroundChange,
  startupTabPreference: initialStartupTabPreference,
  onStartupTabChange,
  language: initialLanguage,
  onLanguageChange,
  initialChatMessage,
  onClearInitialMessage,
  onSendMessage: onSendMessageFromParent,
  activeSection: activeSectionProp,
  onNavigate: onNavigateProp,
  onUserPreferencesChanged, // Callback cuando el asistente actualiza preferencias
  wallpaperPreference: initialWallpaperPreference,
  interfaceStyle: initialInterfaceStyle
}) => {
  // Usar activeSection de las props si está disponible, de lo contrario usar estado local
  const [activeSection, setActiveSection] = useState(() => {
    // Si hay activeSection en props, usarlo CON PRIORIDAD
    if (activeSectionProp) {
      console.log('[FullDashboard] Estado inicial con activeSectionProp:', activeSectionProp);
      return activeSectionProp;
    }
    // Si hay una preferencia de pestaña de inicio que no sea 'default', usarla
    if (initialStartupTabPreference && initialStartupTabPreference !== 'default') {
      return initialStartupTabPreference;
    }
    // De lo contrario, usar el valor guardado en localStorage o 'home' por defecto
    return localStorage.getItem('activeSection') || 'home';
  });

  // Sincronizar activeSection con la prop cuando cambia - SIN condición restrictiva
  useEffect(() => {
    if (activeSectionProp) {
      console.log('[FullDashboard] Sincronizando activeSection a:', activeSectionProp, '(anterior:', activeSection, ')');
      setActiveSection(activeSectionProp);
    }
  }, [activeSectionProp]);

  // Log para detectar cuando llega initialChatMessage
  useEffect(() => {
    if (initialChatMessage) {
      console.log('[FullDashboard] Mensaje inicial recibido:', initialChatMessage);
    }
  }, [initialChatMessage]);
  const [user, setUser] = useState(initialUser);
  const [chatMessages, setChatMessages] = useState([]);
  // Usamos una variable isLoading directamente en lugar del state
  const isLoadingChat = false; // Eliminamos el state setter no utilizado
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // Eliminamos la variable showProfile no utilizada
  const [backgroundSelectorAnchorEl, setBackgroundSelectorAnchorEl] = useState(null);
  const [isUserAuthorized, setIsUserAuthorized] = useState(false);
  // Eliminamos completamente el state no utilizado
  const [chatUpdateTrigger, setChatUpdateTrigger] = useState(0);
  const [mostRecentChatId, setMostRecentChatId] = useState(null);
  const [, setLastActivityTimestamp] = useState(Date.now());
  
  // Estado para la preferencia de pestaña de inicio
  const [startupTabPreference, setStartupTabPreference] = useState(initialStartupTabPreference || 'default');
  
  // Estado para la preferencia de idioma
  const [language, setLanguage] = useState(initialLanguage || 'auto');

  // Estado para la preferencia de wallpaper (usa la prop si está disponible)
  const [wallpaperPreference, setWallpaperPreference] = useState(initialWallpaperPreference || 'appartment');

  // Estado para la preferencia de estilo de interfaz (usa la prop si está disponible)
  const [interfaceStyle, setInterfaceStyle] = useState(initialInterfaceStyle || 'classic');

  // Inicializamos showUpsInNav leyendo el valor persistido en localStorage
  const [showUpsInNav, setShowUpsInNav] = useState(() => {
    return localStorage.getItem('showUpsInNav') === 'true';
  });
  const [showEduInNav, setShowEduInNav] = useState(() => {
    return localStorage.getItem('showEduInNav') === 'true';
  });
  // NUEVO ESTADO PARA MOSTRAR FIN EN LA NAVEGACIÓN
  const [showFinInNav, setShowFinInNav] = useState(() => {
    return localStorage.getItem('showFinInNav') === 'true';
  });
  // Estado para mostrar Health en la navegación
  const [showHealthInNav, setShowHealthInNav] = useState(() => {
    return localStorage.getItem('showHealthInNav') === 'true';
  });
  // Estado para mostrar Fit en la navegación
  const [showFitInNav, setShowFitInNav] = useState(() => {
    return localStorage.getItem('showFitInNav') === 'true';
  });
  // Usamos la sintaxis de desestructuración para indicar que no usamos la variable showPromo
  const [, setShowPromo] = useState(false);
  
  // Estados para SelectApps - CORREGIDO
  const [showSelectApps, setShowSelectApps] = useState(false); // Iniciar en false
  const [selectedApps, setSelectedApps] = useState(['home', 'chat']);
  
  const inactivityTimerRef = useRef(null);
  const messageCache = useRef(new MessageCache());
  const resourceManager = useRef(new ResourceManager());
  const lastNavigationTime = useRef(Date.now());
  const { i18n } = useTranslation();

  const firstName = useMemo(() => {
    return getFirstName(initialUser?.displayName);
  }, [initialUser?.displayName]);

  const backgroundImage = useMemo(() => ({
    aurora: "/images/background/aurora.png",
    curious: "/images/background/curious.png",
    edge: "/images/background/edge.png",
    eureka: "/images/background/eureka.png",
    hacker: "/images/background/hacker.png",
    horizon: "/images/background/horizon.png",
    ignite: "/images/background/ignite.png",
    mind: "/images/background/mind.png",
    neuro: "/images/background/neuro.png",
    odin: "/images/background/odin.png",
    python: "/images/background/python.png",
    vibe: "/images/background/vibe.png"
  }), []);

  useEffect(() => {
    const handlePaymentReturn = async () => {
      const session_id = new URLSearchParams(window.location.search).get('session_id');
      
      if (session_id && session_id.startsWith('cs_')) {
        try {
          const status = await checkProStatus(user.uid);
          if (status.isPro) {
            setErrorMessage('');
            setActiveSection('chat'); 
            setSuccessMessage('Welcome to AGT Pro! Your account has been upgraded successfully.');
          }
        } catch (error) {
          console.error('Error verifying pro status:', error);
          setErrorMessage('Error verifying subscription status. Please contact support.');
        }
        
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    if (user?.uid) {
      handlePaymentReturn();
    }
  }, [user]);

  const [backgroundType, setBackgroundType] = useState(() => {
    const validBackgroundTypes = [
      'system', 'light', 'dark',
      'wallpaper', 'wallpaper-light', 'wallpaper-dark',
      ...Object.keys(backgroundImage)
    ];
    const initialBg = validBackgroundTypes.includes(initialBackgroundType) ? initialBackgroundType : 'system';
    return initialBg;
  });

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const theme = useMemo(() => {
    // Determinar el modo efectivo para el tema
    let effectiveMode = backgroundType;

    // Si es 'system', usar la preferencia del sistema
    if (backgroundType === 'system') {
      effectiveMode = prefersDarkMode ? 'dark' : 'light';
    }

    // Si es cualquier variante de wallpaper, usar 'wallpaper' como modo
    if (backgroundType === 'wallpaper' || backgroundType === 'wallpaper-light' || backgroundType === 'wallpaper-dark') {
      effectiveMode = 'wallpaper';
    }

    // Usar getTheme de theme.js que maneja correctamente el modo wallpaper
    return createTheme(getTheme(effectiveMode));
  }, [backgroundType, prefersDarkMode]);

  const styles = getDashboardStyles(theme, activeSection);

  // Verificar configuración de aplicaciones del usuario - VERSIÓN CORREGIDA Y UNIFICADA
  useEffect(() => {
    const checkUserAppsConfiguration = async () => {
      if (!user?.uid) return;
      
      try {
        
        // Obtener configuración del servidor (fuente única de verdad)
        const result = await getUserSelectedApps(user.uid);
        
        if (result && result.selectedApps && result.selectedApps.length > 0) {
          // El usuario ya tiene configuración guardada

          // Actualizar estados locales
          setSelectedApps(result.selectedApps);
          setShowSelectApps(false);
          
          // Sincronizar localStorage (pero no depender de él)
          localStorage.setItem('selectedApps', JSON.stringify(result.selectedApps));
          localStorage.setItem('defaultApp', result.defaultApp);
          localStorage.setItem('userHasSelectedApps', 'true');
          
          // Actualizar visibilidad de navegación
          updateNavigationVisibility(result.selectedApps);
          
          // Aplicar app predeterminada si corresponde
          if (startupTabPreference === 'default') {
            setActiveSection(result.defaultApp);
            localStorage.setItem('activeSection', result.defaultApp);
          }
          
        } else {
          // El usuario NO tiene configuración guardada

          // Limpiar localStorage para evitar inconsistencias
          localStorage.removeItem('userHasSelectedApps');
          localStorage.removeItem('selectedApps');
          localStorage.removeItem('defaultApp');

          // Mostrar selector
          setShowSelectApps(true);
        }
        
      } catch (error) {
        console.error("Error verificando configuración de apps:", error);

        // En caso de error, ser conservador y mostrar el selector
        localStorage.removeItem('userHasSelectedApps');
        setShowSelectApps(true);
      }
    };
    
    // Función auxiliar para actualizar visibilidad de navegación - ACTUALIZADA CON FIN
    const updateNavigationVisibility = (apps) => {
      const showUps = apps.includes('ups');
      const showEdu = apps.includes('edu');
      const showFin = apps.includes('fin'); // NUEVA LÓGICA PARA FIN
      const showHealth = apps.includes('health');
      const showFit = apps.includes('fit');
      
      setShowUpsInNav(showUps);
      setShowEduInNav(showEdu);
      setShowFinInNav(showFin); // NUEVO SETTER PARA FIN
      setShowHealthInNav(showHealth);
      setShowFitInNav(showFit);
      
      localStorage.setItem('showUpsInNav', showUps.toString());
      localStorage.setItem('showEduInNav', showEdu.toString());
      localStorage.setItem('showFinInNav', showFin.toString()); // NUEVO PARA FIN
      localStorage.setItem('showHealthInNav', showHealth.toString());
      localStorage.setItem('showFitInNav', showFit.toString());
    };
    
    checkUserAppsConfiguration();
  }, [user?.uid, startupTabPreference]);

  // Actualizar startupTabPreference cuando cambie desde las props
  useEffect(() => {
    if (initialStartupTabPreference) {
      setStartupTabPreference(initialStartupTabPreference);
    }
  }, [initialStartupTabPreference]);

  // Actualizar language cuando cambie desde las props
  useEffect(() => {
    if (initialLanguage) {
      setLanguage(initialLanguage);
    }
  }, [initialLanguage]);

  const resetInactivityTimer = useCallback(() => {
    const now = Date.now();
    setLastActivityTimestamp(now);

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Solo aplicar el temporizador de inactividad si la preferencia de pestaña de inicio es 'default'
    if (startupTabPreference === 'default') {
      inactivityTimerRef.current = setTimeout(() => {
        if (activeSection === 'chat') {
          setActiveSection('home');
          localStorage.setItem('activeSection', 'home');
        }
        // Si la sección es "ups" (u otra), no se cambia automáticamente.
      }, INACTIVITY_TIMEOUT);
    }
  }, [activeSection, startupTabPreference]);

  useEffect(() => {
    setUser(initialUser);
    setIsUserAuthorized(isAuthorized(initialUser?.email));

    // Capturamos las referencias actuales para el cleanup
    const currentMessageCache = messageCache.current;
    const currentResourceManager = resourceManager.current;

    return () => {
      currentMessageCache.clear();
      currentResourceManager.clearAll();
    };
  }, [initialUser]);

  // Este useEffect ya NO es necesario porque todas las preferencias
  // se cargan en App.js y se pasan como props.
  // Solo mantener sincronización con props si cambian.
  useEffect(() => {
    // Si las props cambian, actualizar los estados locales
    if (initialBackgroundType) {
      setBackgroundType(initialBackgroundType);
    }
    if (initialStartupTabPreference) {
      setStartupTabPreference(initialStartupTabPreference);
    }
    if (initialLanguage) {
      setLanguage(initialLanguage);
    }
    if (initialWallpaperPreference) {
      setWallpaperPreference(initialWallpaperPreference);
    }
    if (initialInterfaceStyle) {
      setInterfaceStyle(initialInterfaceStyle);
    }
  }, [initialBackgroundType, initialStartupTabPreference, initialLanguage, initialWallpaperPreference, initialInterfaceStyle]);

  useEffect(() => {
    const handleUserActivity = () => {
      resetInactivityTimer();
    };

    const eventTypes = ['mousemove', 'keypress', 'click', 'scroll'];
    
    eventTypes.forEach(eventType => {
      window.addEventListener(eventType, handleUserActivity);
    });

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      eventTypes.forEach(eventType => {
        window.removeEventListener(eventType, handleUserActivity);
      });
    };
  }, [resetInactivityTimer]);

  // Nueva función para limpiar el ID del chat más reciente
  const clearMostRecentChatId = useCallback(() => {
    setMostRecentChatId(null);
  }, []);

  const handleNavigate = useCallback((section) => {
    const now = Date.now();
    if (now - lastNavigationTime.current >= NAVIGATION_DEBOUNCE_TIME) {
      lastNavigationTime.current = now;

      // Si estamos navegando a home, limpiar el ID del chat más reciente
      if (section === 'home') {
        setMostRecentChatId(null);
      }

      // Si onNavigateProp existe, usarlo (viene de App.js)
      if (onNavigateProp) {
        onNavigateProp(section);
      } else {
        // De lo contrario, manejar navegación localmente
        setActiveSection(section);
      }

      if (section === 'chat' && chatMessages.length === 0) {
        setChatMessages([]);
      }

      localStorage.setItem('activeSection', section);
      resetInactivityTimer();
    }
  }, [chatMessages.length, resetInactivityTimer, onNavigateProp]);

  // Añadir listener para el evento personalizado de navegación
  useEffect(() => {
    const handleNavigateEvent = (event) => {
      if (event.detail && event.detail.section) {
        handleNavigate(event.detail.section);
      }
    };

    window.addEventListener('navigateToSection', handleNavigateEvent);
    
    return () => {
      window.removeEventListener('navigateToSection', handleNavigateEvent);
    };
  }, [handleNavigate]);

  // Añadir listener para el evento openSettings
  useEffect(() => {
    const handleOpenSettings = () => {
      setBackgroundSelectorAnchorEl(document.body); // Simplemente establecemos un valor no nulo
    };

    window.addEventListener('openSettings', handleOpenSettings);
    
    return () => {
      window.removeEventListener('openSettings', handleOpenSettings);
    };
  }, []);

  const handleProfileClick = useCallback(() => {
    const now = Date.now();
    if (now - lastNavigationTime.current >= NAVIGATION_DEBOUNCE_TIME) {
      lastNavigationTime.current = now;

      // Si onNavigateProp existe, usarlo (viene de App.js)
      if (onNavigateProp) {
        onNavigateProp('profile');
      } else {
        // De lo contrario, manejar navegación localmente
        setActiveSection('profile');
      }

      localStorage.setItem('activeSection', 'profile');
      resetInactivityTimer();
    }
  }, [resetInactivityTimer, onNavigateProp]);

  const handleSendMessage = useCallback(async (userTextOrObj) => {
    try {
      // IMPORTANTE: Limpiar mostRecentChatId para asegurar que ChatViewNest
      // procese el initialChatMessage en lugar de cargar un chat existente
      setMostRecentChatId(null);

      // Llamar al handleSendMessage del padre (App.js) que:
      // 1. Cambiará a la sección de chat
      // 2. Establecerá initialChatMessage
      // ChatViewNest se encargará de crear el chat, mostrar el mensaje,
      // el typing indicator y manejar el streaming
      if (onSendMessageFromParent) {
        onSendMessageFromParent(userTextOrObj);
      }

      // IMPORTANTE: NO incrementar chatUpdateTrigger aquí porque se usa como key
      // y causaría que ChatViewNest se desmonte y pierda el initialChatMessage
      resetInactivityTimer();

      return { success: true };
    } catch (error) {
      console.error("Error en handleSendMessage:", error);
      setErrorMessage("Error sending message. Please try again.");
      return { success: false, error };
    }
  }, [onSendMessageFromParent, resetInactivityTimer]);

  const handleBackgroundClick = useCallback((event) => {
    setBackgroundSelectorAnchorEl(event.currentTarget);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleBackgroundClose = useCallback(() => {
    setBackgroundSelectorAnchorEl(null);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleBackgroundChange = useCallback(async (newBackgroundType) => {
    const validBackgroundTypes = [
      'system', 'light', 'dark',
      'wallpaper', 'wallpaper-light', 'wallpaper-dark',
      ...Object.keys(backgroundImage)
    ];
    if (validBackgroundTypes.includes(newBackgroundType)) {
      try {
        await setUserBackgroundPreference(user.uid, newBackgroundType);
        setBackgroundType(newBackgroundType);
        onBackgroundChange(newBackgroundType);
      } catch (error) {
        setErrorMessage("Error saving background preference. Please try again.");
      }
    } else {
      setErrorMessage("Invalid background type.");
    }
    resetInactivityTimer();
  }, [user, onBackgroundChange, backgroundImage, resetInactivityTimer]);

  // Función para manejar el cambio de preferencia de pestaña de inicio
  const handleStartupTabChange = useCallback(async (newStartupTab) => {
    if (user?.uid) {
      try {
        await setUserStartupTabPreference(user.uid, newStartupTab);
        setStartupTabPreference(newStartupTab);
        
        // Si se selecciona una pestaña específica, aplicarla inmediatamente
        if (newStartupTab !== 'default') {
          setActiveSection(newStartupTab);
          localStorage.setItem('activeSection', newStartupTab);
        }
        
        // Notificar al componente padre si existe el callback
        if (onStartupTabChange) {
          onStartupTabChange(newStartupTab);
        }
      } catch (error) {
        setErrorMessage("Error saving startup tab preference. Please try again.");
      }
    }
    resetInactivityTimer();
  }, [user, resetInactivityTimer, onStartupTabChange]);

  // Función para manejar el cambio de preferencia de idioma
  const handleLanguageChange = useCallback(async (newLanguage) => {
    if (user?.uid) {
      try {
        await setUserLanguagePreference(user.uid, newLanguage);
        setLanguage(newLanguage);

        // Aplicar el cambio de idioma
        if (newLanguage === 'auto') {
          const browserLang = navigator.language.split('-')[0];
          i18n.changeLanguage(browserLang);
        } else {
          i18n.changeLanguage(newLanguage);
        }

        // Notificar al componente padre si existe el callback
        if (onLanguageChange) {
          onLanguageChange(newLanguage);
        }
      } catch (error) {
        setErrorMessage("Error saving language preference. Please try again.");
      }
    }
    resetInactivityTimer();
  }, [user, resetInactivityTimer, onLanguageChange, i18n]);

  // Función para manejar el cambio de preferencia de wallpaper
  const handleWallpaperChange = useCallback((newWallpaper) => {
    setWallpaperPreference(newWallpaper);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const getCurrentBackground = useCallback(() => {
    // Helper function to get the correct wallpaper image
    const getWallpaperImage = (isDayTime) => {
      const basePath = '/images/wallpaper/';

      // Map wallpaper preferences to their image files
      const wallpaperMap = {
        appartment: {
          day: `${basePath}wallpaperday-appartment.png`,
          night: `${basePath}wallpapernight-appartment.png`
        },
        office: {
          day: `${basePath}wallpaperday-office.png`,
          night: `${basePath}wallpapernight-office.png`
        },
        starbucks: {
          day: `${basePath}wallpaperday-starbucks.png`,
          night: `${basePath}wallpapernight-starbucks.png`
        }
      };

      const selectedWallpaper = wallpaperMap[wallpaperPreference] || wallpaperMap.appartment;
      return isDayTime ? selectedWallpaper.day : selectedWallpaper.night;
    };

    // Wallpaper con System mode: usar hora del día
    if (backgroundType === 'wallpaper') {
      const hour = new Date().getHours();
      const isDayTime = hour >= 6 && hour < 18;
      const wallpaper = getWallpaperImage(isDayTime);
      return wallpaper;
    }

    // Wallpaper con Light mode: siempre día
    if (backgroundType === 'wallpaper-light') {
      const wallpaper = getWallpaperImage(true);
      return wallpaper;
    }

    // Wallpaper con Dark mode: siempre noche
    if (backgroundType === 'wallpaper-dark') {
      const wallpaper = getWallpaperImage(false);
      return wallpaper;
    }

    // Sin wallpaper
    if (['light', 'dark', 'system'].includes(backgroundType)) {
      return null;
    }

    // Otros backgrounds personalizados (si existen)
    return backgroundImage[backgroundType];
  }, [backgroundType, wallpaperPreference, backgroundImage]);

  const handleLogout = useCallback(() => {
    localStorage.setItem('activeSection', 'home');
    // Reiniciamos también los flags al cerrar sesión - ACTUALIZADO CON FIN
    localStorage.setItem('showUpsInNav', 'false');
    localStorage.setItem('showFinInNav', 'false'); // NUEVO PARA FIN
    localStorage.setItem('showFitInNav', 'false');
    localStorage.setItem('userHasSelectedApps', 'false');
    setShowUpsInNav(false);
    setShowFinInNav(false); // NUEVO PARA FIN
    setShowFitInNav(false);
    messageCache.current.clear();
    resourceManager.current.clearAll();
    onLogout();
  }, [onLogout]);

  // Callback para abrir AGT Ups y hacerlo permanente
  const handleOpenAGTUps = useCallback(() => {
    setActiveSection('ups');
    setShowUpsInNav(true);
    localStorage.setItem('showUpsInNav', 'true');
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleOpenAGTEdu = useCallback(() => {
    setActiveSection('edu');
    setShowEduInNav(true);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // NUEVO CALLBACK PARA ABRIR AGT FIN
  const handleOpenAGTFin = useCallback(() => {
    setActiveSection('fin');
    setShowFinInNav(true);
    localStorage.setItem('showFinInNav', 'true');
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Callback para abrir AGT Health y hacerlo visible en la navegación
  const handleOpenAGTHealth = useCallback(() => {
    setActiveSection('health');
    setShowHealthInNav(true);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Callback para abrir AGT Fit y hacerlo visible en la navegación
  const handleOpenAGTFit = useCallback(() => {
    setActiveSection('fit');
    setShowFitInNav(true);
    localStorage.setItem('showFitInNav', 'true');
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleOpenAGTPromo = useCallback(() => {
    setActiveSection('promo');
    setShowPromo(true);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Manejar la selección de aplicaciones - VERSIÓN MEJORADA Y CORREGIDA
  const handleAppSelectionSave = async ({ selectedApps: newSelectedApps, defaultApp: newDefaultApp }) => {
    try {
      
      // Primero guardar en la base de datos (fuente única de verdad)
      if (user?.uid) {
        await saveUserSelectedApps(user.uid, newSelectedApps, newDefaultApp);
      }
      
      // Luego actualizar estado local
      setSelectedApps(newSelectedApps);
      setShowSelectApps(false);
      
      // Sincronizar localStorage
      localStorage.setItem('selectedApps', JSON.stringify(newSelectedApps));
      localStorage.setItem('defaultApp', newDefaultApp);
      localStorage.setItem('userHasSelectedApps', 'true');
      
      // Actualizar la navegación - ACTUALIZADO CON FIN
      const showUpsInNav = newSelectedApps.includes('ups');
      const showEduInNav = newSelectedApps.includes('edu');
      const showFinInNav = newSelectedApps.includes('fin'); // NUEVO PARA FIN
      const showHealthInNav = newSelectedApps.includes('health');
      const showFitInNav = newSelectedApps.includes('fit');
      
      setShowUpsInNav(showUpsInNav);
      setShowEduInNav(showEduInNav);
      setShowFinInNav(showFinInNav); // NUEVO PARA FIN
      setShowHealthInNav(showHealthInNav);
      setShowFitInNav(showFitInNav);
      
      localStorage.setItem('showUpsInNav', showUpsInNav.toString());
      localStorage.setItem('showEduInNav', showEduInNav.toString());
      localStorage.setItem('showFinInNav', showFinInNav.toString()); // NUEVO PARA FIN
      localStorage.setItem('showHealthInNav', showHealthInNav.toString());
      localStorage.setItem('showFitInNav', showFitInNav.toString());
      
      // Cambiar a la app predeterminada
      setActiveSection(newDefaultApp);
      localStorage.setItem('activeSection', newDefaultApp);
      
      // Actualizar la preferencia de pestaña de inicio
      handleStartupTabChange(newDefaultApp);
      
      
    } catch (error) {
      console.error('Error saving app selection:', error);
      setErrorMessage('Error saving your preferences. Please try again.');
      
      // En caso de error, no cerrar el modal para que el usuario pueda intentar de nuevo
      // setShowSelectApps(false); // Comentamos esta línea
    }
  };

  // Si la sección activa es "ups" pero showUpsInNav es false, activar el flag
  useEffect(() => {
    if (activeSection === 'ups' && !showUpsInNav) {
      setShowUpsInNav(true);
      localStorage.setItem('showUpsInNav', 'true');
    }
  }, [activeSection, showUpsInNav]);

  // NUEVO EFECTO PARA FIN - Si la sección activa es "fin" pero showFinInNav es false, activar el flag
  useEffect(() => {
    if (activeSection === 'fin' && !showFinInNav) {
      setShowFinInNav(true);
      localStorage.setItem('showFinInNav', 'true');
    }
  }, [activeSection, showFinInNav]);

  // Si la sección activa es "health" pero showHealthInNav es false, activar el flag
  useEffect(() => {
    if (activeSection === 'health' && !showHealthInNav) {
      setShowHealthInNav(true);
    }
  }, [activeSection, showHealthInNav]);

  // Si la sección activa es "fit" pero showFitInNav es false, activar el flag
  useEffect(() => {
    if (activeSection === 'fit' && !showFitInNav) {
      setShowFitInNav(true);
      localStorage.setItem('showFitInNav', 'true');
    }
  }, [activeSection, showFitInNav]);

  // Listener para cambios en la configuración de aplicaciones - ACTUALIZADO CON FIN
  useEffect(() => {
    const handleAppsConfigurationChanged = (event) => {
      const { selectedApps, showUpsInNav, showEduInNav, showFinInNav, showHealthInNav, showFitInNav } = event.detail;
      
      
      // Actualizar estados locales
      setSelectedApps(selectedApps);
      setShowUpsInNav(showUpsInNav);
      setShowEduInNav(showEduInNav);
      setShowFinInNav(showFinInNav); // NUEVO PARA FIN
      setShowHealthInNav(showHealthInNav);
      setShowFitInNav(showFitInNav);
      
      // Forzar re-render del NavigationBar
      setChatUpdateTrigger(prev => prev + 1);
    };

    window.addEventListener('appsConfigurationChanged', handleAppsConfigurationChanged);
    
    return () => {
      window.removeEventListener('appsConfigurationChanged', handleAppsConfigurationChanged);
    };
  }, []);

  const renderContent = useCallback(() => {
    switch (activeSection) {
      case 'home':
        return (
          <Box sx={{
            ...styles.homeBox,
            paddingBottom: '70px',
            paddingTop: '0px', // Changed from 10px to 0px
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
          }}>
            <HomeInput
              onSendMessage={handleSendMessage}
              onOpenAGTPromo={handleOpenAGTPromo}
              isPro={user?.isPro}
              userId={user?.uid}
              selectedApps={selectedApps}
            />
          </Box>
        );
      case 'chat':
        return (
          <Box sx={styles.chatBox}>
            <ChatViewNest
              key={chatUpdateTrigger}
              user={user}
              userName={firstName}
              isAuthorized={isUserAuthorized}
              onSendMessage={handleSendMessage}
              isLimited={false}
              initialView={chatMessages.length === 0}
              messages={chatMessages}
              isLoading={isLoadingChat}
              onNavigate={handleNavigate}
              themeMode={theme.palette.mode}
              mostRecentChatId={mostRecentChatId}
              onClearMostRecentChatId={clearMostRecentChatId}
              onOpenAGTPromo={handleOpenAGTPromo}
              initialChatMessage={initialChatMessage}
              onClearInitialMessage={onClearInitialMessage}
              onUserPreferencesChanged={onUserPreferencesChanged}
            />
          </Box>
        );
      case 'profile':
        return (
          <Box sx={{ 
            backgroundColor: 'inherit', // Asegura que herede el color de fondo 
            height: '100%',
            width: '100%'
          }}>
          <ProfileDashboard 
            user={user}
            onLogout={handleLogout}
            backgroundType={backgroundType}
            language={language}
            onLanguageChange={handleLanguageChange}
          />
          </Box>
        );
      case 'ups':
        return <AGTUps user={user} />;
      case 'edu':
        return <AGTEdu />;
      case 'fin': // NUEVO CASO PARA AGT FIN
        return <AGTFin user={user} />;
      case 'health':
        return <AGTHealth user={user} />;
      case 'fit':
        return <AGTFit user={{...user, firstName: firstName}} />;
      case 'promo':
        return (
          <Box sx={{
            position: 'relative',
            height: '100%',
            width: '100%',
            overflow: 'hidden', // Cambia a hidden para prevenir scroll global
          }}>
            <AGTPromo 
              onOpenAGTUps={handleOpenAGTUps} 
              onOpenAGTEdu={handleOpenAGTEdu}
              onOpenAGTFin={handleOpenAGTFin} // NUEVO PROP PARA ABRIR FIN
              onOpenAGTHealth={handleOpenAGTHealth}
              onOpenAGTFit={handleOpenAGTFit} // Añadimos el prop para abrir Fit
              userId={user.uid}
            />
          </Box>
        );
      default:
        return null;
    }
  }, [
    activeSection,
    user,
    firstName,
    isUserAuthorized,
    handleSendMessage,
    chatMessages,
    isLoadingChat,
    handleNavigate,
    theme,
    handleLogout,
    backgroundType,
    chatUpdateTrigger,
    mostRecentChatId,
    clearMostRecentChatId,
    handleOpenAGTUps,
    handleOpenAGTEdu,
    handleOpenAGTFin, // NUEVO HANDLER PARA ABRIR AGT FIN
    handleOpenAGTHealth,
    handleOpenAGTFit, // Añadimos el handler para abrir AGT Fit
    handleOpenAGTPromo,
    styles,
    language,
    handleLanguageChange,
    selectedApps
  ]);

  return (
    <ThemeProvider theme={theme}>
      <StyledContainer
        style={{
          backgroundImage: (() => {
            const bg = getCurrentBackground();
            if (!bg) return 'none';
            return `url("${bg}")`;
          })(),
          backgroundColor: getCurrentBackground()
            ? 'transparent'
            : (backgroundType === 'system'
                ? (prefersDarkMode ? '#171717' : '#FFFFFF')
                : (backgroundType === 'light' ? '#FFFFFF' : '#171717'))
        }}
      >
        <StyledWindowBar>
          <TopBarContent 
            handleBackgroundClick={handleBackgroundClick}
            handleProfileClick={handleProfileClick}
            user={user}
            onNavigate={handleNavigate}
            activeSection={activeSection}
            showUpsInNav={showUpsInNav}
            showEduInNav={showEduInNav}
            showFinInNav={showFinInNav} // NUEVO PROP PARA FIN
            showHealthInNav={showHealthInNav}
            showFitInNav={showFitInNav}
            selectedApps={selectedApps}
          />
          <ContentArea>
            <Fade in={true} timeout={300}>
              <Box sx={styles.contentBox}>
                {renderContent()}
              </Box>
            </Fade>
          </ContentArea>
        </StyledWindowBar>

        <SettingsDialog
          open={Boolean(backgroundSelectorAnchorEl)}
          onClose={handleBackgroundClose}
          onBackgroundChange={handleBackgroundChange}
          currentBackground={backgroundType}
          onStartupTabChange={handleStartupTabChange}
          currentStartupTab={startupTabPreference}
          onLanguageChange={handleLanguageChange}
          currentLanguage={language}
          onWallpaperChange={handleWallpaperChange}
          currentWallpaper={wallpaperPreference}
          selectedApps={selectedApps}
          user={user}
        />

        {/* Modal de selección de aplicaciones */}
        <SelectApps
          open={showSelectApps}
          onClose={() => {
            setShowSelectApps(false);
          }}
          onSave={(data) => {
            handleAppSelectionSave(data);
            // No cerrar aquí, se cierra en handleAppSelectionSave si todo sale bien
          }}
        />

        <Snackbar 
          open={!!errorMessage} 
          autoHideDuration={6000} 
          onClose={() => setErrorMessage('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={styles.snackbarContainer}
        >
          <Alert 
            onClose={() => setErrorMessage('')} 
            severity="error"
            sx={styles.snackbarAlert}
          >
            {errorMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSuccessMessage('')}
            severity="success"
            sx={{ width: '100%' }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </StyledContainer>
    </ThemeProvider>
  );
};

export default React.memo(FullDashboard);